import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Label,
  Textarea,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import {
  adminPaymentsApi,
  type PaymentRequest,
} from "@/services/api";
import {
  ArrowLeft,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  DollarSign,
  Send,
  FileText,
  Clock,
  Copy,
  Building2,
  TrendingUp,
  RefreshCw,
  Wallet,
  Globe,
} from "lucide-react";
import { formatDate, formatDateTime, cn } from "@/lib/utils";
import { toast } from "sonner";

// ─── Status Config ────────────────────────────────────────

const statusConfig: Record<
  string,
  {
    label: string;
    variant: "default" | "secondary" | "success" | "warning" | "destructive";
    description: string;
  }
> = {
  DRAFT: { label: "Rascunho", variant: "secondary", description: "Pagamento em rascunho" },
  PENDING_APPROVAL: { label: "Aguardando Aprovação", variant: "warning", description: "Aguardando aprovação do administrador" },
  APPROVED: { label: "Aprovado", variant: "success", description: "Aprovado, aguardando processamento" },
  PROCESSING: { label: "Processando", variant: "warning", description: "Processando cotação e payout" },
  AWAITING_DEPOSIT: { label: "Aguardando Depósito", variant: "warning", description: "Aguardando depósito USDC no provedor" },
  DEPOSITED: { label: "Depositado", variant: "default", description: "USDC depositado, processando payout" },
  COMPLETED: { label: "Concluído", variant: "success", description: "Pagamento concluído com sucesso" },
  FAILED: { label: "Falhou", variant: "destructive", description: "Erro no processamento" },
  CANCELLED: { label: "Cancelado", variant: "secondary", description: "Pagamento cancelado" },
  REJECTED: { label: "Rejeitado", variant: "destructive", description: "Pagamento rejeitado pelo administrador" },
};

const RAIL_LABELS: Record<string, string> = {
  wire: "Wire Transfer (USD)",
  sepa: "SEPA (EUR)",
  spei: "SPEI (MXN)",
};

// ─── Helpers ──────────────────────────────────────────────

function formatCurrencyValue(value: string | number | null | undefined, currency = "USD"): string {
  if (!value) return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "-";
  const currencyMap: Record<string, string> = { USD: "en-US", EUR: "de-DE", MXN: "es-MX" };
  const locale = currencyMap[currency] || "en-US";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(num);
}

function formatUsdValue(value: string | number | null | undefined): string {
  if (!value) return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(num);
}

function formatUsdcAmount(value: string | number | null | undefined): string {
  if (!value) return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "-";
  return `${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 6 })} USDC`;
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).then(() => {
    toast.success("Copiado!", {
      style: { background: "#495565", color: "#ffffff" },
    });
  }).catch(() => {
    console.error("Erro ao copiar para clipboard");
  });
}

// ─── Main Component ───────────────────────────────────────

export function AdminPaymentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [payment, setPayment] = useState<PaymentRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Dialog states
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [approveNotes, setApproveNotes] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  const loadPayment = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await adminPaymentsApi.getPayment(id);
      setPayment(data);
    } catch (error) {
      console.error("Erro ao carregar pagamento:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadPayment();
  }, [loadPayment]);

  async function handleApprove() {
    if (!payment) return;
    try {
      setActionLoading(true);
      await adminPaymentsApi.approvePayment(payment.id, {
        adminNotes: approveNotes || undefined,
      });
      setShowApproveDialog(false);
      loadPayment();
      toast.success("Pagamento aprovado com sucesso", {
        style: { background: "#495565", color: "#ffffff" },
      });
    } catch (error) {
      console.error("Erro ao aprovar pagamento:", error);
      toast.error("Erro ao aprovar pagamento", {
        description: error instanceof Error ? error.message : "Tente novamente",
        style: { background: "#495565", color: "#ffffff" },
      });
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReject() {
    if (!payment || !rejectReason.trim()) return;
    try {
      setActionLoading(true);
      await adminPaymentsApi.rejectPayment(payment.id, {
        reason: rejectReason,
      });
      setShowRejectDialog(false);
      loadPayment();
      toast.success("Pagamento rejeitado", {
        style: { background: "#495565", color: "#ffffff" },
      });
    } catch (error) {
      console.error("Erro ao rejeitar pagamento:", error);
      toast.error("Erro ao rejeitar pagamento", {
        description: error instanceof Error ? error.message : "Tente novamente",
        style: { background: "#495565", color: "#ffffff" },
      });
    } finally {
      setActionLoading(false);
    }
  }

  // ─── Loading ──────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <AlertTriangle className="w-12 h-12 text-muted-foreground" />
        <p className="text-muted-foreground">Pagamento não encontrado</p>
        <Button onClick={() => navigate("/admin/payments")}>Voltar para Lista</Button>
      </div>
    );
  }

  const status = statusConfig[payment.status] || {
    label: payment.status,
    variant: "secondary" as const,
    description: "",
  };

  // Computed values
  const beneficiaryName =
    payment.beneficiary?.beneficiaryName || payment.inlineBeneficiaryName || "-";
  const bankName =
    payment.inlineBankName || "-";
  const isAwaitingDeposit = [
    "AWAITING_DEPOSIT",
    "DEPOSITED",
    "COMPLETED",
  ].includes(payment.status);
  const isCompleted = payment.status === "COMPLETED";
  const isPendingApproval = payment.status === "PENDING_APPROVAL";

  // Service fee details
  const serviceFee = payment.serviceFeeAmount ? parseFloat(payment.serviceFeeAmount) : 0;
  const providerActualFee = payment.providerActualFee ? parseFloat(payment.providerActualFee) : 0;
  const netMargin = serviceFee - providerActualFee;

  // ─── Render ───────────────────────────────────────────

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Send className="h-5 w-5 sm:h-6 sm:w-6" />}
        title={`Pagamento ${payment.paymentCode}`}
        description={status.description}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={loadPayment}
              disabled={loading}
            >
              <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
              Atualizar
            </Button>
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={() => navigate("/admin/payments")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            {isPendingApproval && (
              <>
                <Button
                  className="bg-green-500 hover:bg-green-600 text-white border-0"
                  onClick={() => setShowApproveDialog(true)}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Aprovar
                </Button>
                <Button
                  className="bg-red-500 hover:bg-red-600 text-white border-0"
                  onClick={() => setShowRejectDialog(true)}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Rejeitar
                </Button>
              </>
            )}
          </div>
        }
      />

      {/* Status Banner */}
      <Card
        className={cn(
          "border-l-4",
          status.variant === "success" && "border-l-green-500 bg-green-50/30",
          status.variant === "warning" && "border-l-orange-500 bg-orange-50/30",
          status.variant === "destructive" && "border-l-red-500 bg-red-50/30",
          status.variant === "secondary" && "border-l-gray-500 bg-gray-50/30",
          status.variant === "default" && "border-l-blue-500 bg-blue-50/30"
        )}
      >
        <CardContent className="pt-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <Badge variant={status.variant} className="text-sm">
                {status.label}
              </Badge>
              <span className="text-sm text-muted-foreground">
                Criado em {formatDateTime(payment.createdAt)}
              </span>
            </div>
            {payment.rejectionReason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
                <p className="text-sm font-medium text-red-800">Motivo da Rejeicao:</p>
                <p className="text-sm text-red-700 mt-1">{payment.rejectionReason}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Dados do Pagamento */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Dados do Pagamento
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-muted-foreground">Valor</Label>
                <p className="text-xl font-bold">
                  {formatCurrencyValue(payment.amount, payment.currency)}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground">Moeda / Rail</Label>
                <p className="font-medium">
                  {payment.currency} - {RAIL_LABELS[payment.paymentRail] || payment.paymentRail}
                </p>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label className="text-muted-foreground">Fornecedor</Label>
                  <p className="font-medium">{payment.supplierName || "-"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Invoice</Label>
                  <p className="font-medium">{payment.invoiceNumber || "-"}</p>
                </div>
              </div>
            </div>

            {payment.description && (
              <div className="border-t pt-4">
                <Label className="text-muted-foreground">Descrição</Label>
                <p className="text-sm mt-1">{payment.description}</p>
              </div>
            )}

            {payment.exchangeRate && (
              <div className="border-t pt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label className="text-muted-foreground">Cotação</Label>
                    <p className="font-medium">
                      1 USDC = {parseFloat(payment.exchangeRate).toFixed(4)} {payment.currency}
                    </p>
                  </div>
                  {payment.amountUsdc && (
                    <div>
                      <Label className="text-muted-foreground">Total USDC</Label>
                      <p className="font-medium">{formatUsdcAmount(payment.amountUsdc)}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Company info */}
            {(payment as PaymentRequest & { companyName?: string }).companyName && (
              <div className="border-t pt-4">
                <Label className="text-muted-foreground">Empresa</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <p className="font-medium">
                    {(payment as PaymentRequest & { companyName?: string }).companyName}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Beneficiario */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Beneficiário
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-muted-foreground">Nome</Label>
              <p className="font-medium">{beneficiaryName}</p>
            </div>

            {payment.beneficiary?.nickname && (
              <div>
                <Label className="text-muted-foreground">Apelido</Label>
                <p className="text-sm">{payment.beneficiary.nickname}</p>
              </div>
            )}

            <div className="border-t pt-4 space-y-3">
              {/* Wire fields */}
              {payment.paymentRail === "wire" && (
                <>
                  {(payment.inlineRoutingNumber) && (
                    <div>
                      <Label className="text-muted-foreground">Routing Number</Label>
                      <p className="font-mono text-sm">{payment.inlineRoutingNumber}</p>
                    </div>
                  )}
                  {(payment.inlineBankAccountNumber) && (
                    <div>
                      <Label className="text-muted-foreground">Conta Bancária</Label>
                      <p className="font-mono text-sm">{payment.inlineBankAccountNumber}</p>
                    </div>
                  )}
                  {bankName !== "-" && (
                    <div>
                      <Label className="text-muted-foreground">Banco</Label>
                      <p className="text-sm">{bankName}</p>
                    </div>
                  )}
                  {payment.inlineAddressStreet && (
                    <div>
                      <Label className="text-muted-foreground">Endereço</Label>
                      <p className="text-sm">
                        {[
                          payment.inlineAddressStreet,
                          payment.inlineAddressCity,
                          payment.inlineAddressState,
                          payment.inlineAddressPostalCode,
                          payment.inlineAddressCountry,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* SEPA fields */}
              {payment.paymentRail === "sepa" && (
                <>
                  {payment.inlineIban && (
                    <div>
                      <Label className="text-muted-foreground">IBAN</Label>
                      <p className="font-mono text-sm">{payment.inlineIban}</p>
                    </div>
                  )}
                  {payment.inlineBic && (
                    <div>
                      <Label className="text-muted-foreground">BIC/SWIFT</Label>
                      <p className="font-mono text-sm">{payment.inlineBic}</p>
                    </div>
                  )}
                  {payment.inlineSepaCountry && (
                    <div>
                      <Label className="text-muted-foreground">País</Label>
                      <p className="text-sm">{payment.inlineSepaCountry}</p>
                    </div>
                  )}
                  {(payment.inlineFirstName || payment.inlineLastName) && (
                    <div>
                      <Label className="text-muted-foreground">Titular</Label>
                      <p className="text-sm">
                        {[payment.inlineFirstName, payment.inlineLastName].filter(Boolean).join(" ")}
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* SPEI fields */}
              {payment.paymentRail === "spei" && (
                <>
                  {payment.inlineSpeiClabe && (
                    <div>
                      <Label className="text-muted-foreground">CLABE</Label>
                      <p className="font-mono text-sm">{payment.inlineSpeiClabe}</p>
                    </div>
                  )}
                  {payment.inlineSpeiBankCode && (
                    <div>
                      <Label className="text-muted-foreground">Código do Banco</Label>
                      <p className="font-mono text-sm">{payment.inlineSpeiBankCode}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Instrucoes de Deposito */}
        {isAwaitingDeposit && payment.depositAddress && (
          <Card className="lg:col-span-2 border-orange-500/20 bg-orange-50/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-600">
                <Wallet className="h-5 w-5" />
                Instruções de Depósito
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Endereço da Wallet</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-sm font-mono bg-muted/50 px-2 py-1 rounded break-all">
                      {payment.depositAddress}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(payment.depositAddress!)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Valor a Depositar</Label>
                  <p className="text-lg font-bold mt-1">
                    {formatUsdcAmount(payment.depositAmount)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Rede</Label>
                  <p className="font-medium capitalize mt-1">
                    {payment.depositPaymentRail || "-"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Hash da Transação</Label>
                  {payment.depositTxHash ? (
                    <div className="flex items-center gap-2 mt-1">
                      <code className="text-sm font-mono bg-muted/50 px-2 py-1 rounded truncate max-w-[200px]">
                        {payment.depositTxHash}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(payment.depositTxHash!)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground mt-1">Aguardando envio</p>
                  )}
                </div>
              </div>

              {/* Deposit status badge */}
              <div className="pt-2">
                {payment.depositTxHash ? (
                  <Badge variant="success" className="flex items-center gap-1 w-fit">
                    <CheckCircle className="w-3 h-3" />
                    USDC depositado
                  </Badge>
                ) : (
                  <Badge variant="warning" className="flex items-center gap-1 w-fit">
                    <Clock className="w-3 h-3" />
                    Aguardando depósito USDC
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Custos da Operacao (admin only) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Custos da Operação
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-muted-foreground">Taxa de serviço</span>
                  {payment.serviceFeeType && (
                    <span className="text-xs text-muted-foreground ml-1">
                      ({payment.serviceFeeType === "FIXED"
                        ? "fixo"
                        : `${(parseFloat(payment.serviceFeeRate || "0") * 100).toFixed(2)}%`})
                    </span>
                  )}
                </div>
                <span className="font-medium">{formatUsdValue(payment.serviceFeeAmount)}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Taxa da rede (flat fee)</span>
                <span className="font-medium">{formatUsdValue(payment.providerFlatFeeUsd)}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Custo provedor estimado</span>
                <span className="font-medium text-muted-foreground">
                  {/* This would be amount * providerFeePercentage from gateway config */}
                  -
                </span>
              </div>

              {isCompleted && payment.providerActualFee && (
                <>
                  <div className="border-t pt-3">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Fee real do provedor</span>
                      <span className="font-medium text-red-500">
                        {formatUsdValue(payment.providerActualFee)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="font-medium">Margem líquida</span>
                    <span
                      className={cn(
                        "font-bold",
                        netMargin >= 0 ? "text-green-600" : "text-red-600"
                      )}
                    >
                      {formatUsdValue(netMargin)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Resultado (quando concluído) */}
        {isCompleted && (payment.finalFiatAmount || payment.initialCryptoAmount) && (
          <Card className="border-green-500/20 bg-green-50/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                Resultado
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                {payment.initialCryptoAmount && (
                  <div>
                    <Label className="text-muted-foreground">USDC Enviado</Label>
                    <p className="text-lg font-bold">
                      {formatUsdcAmount(payment.initialCryptoAmount)}
                    </p>
                  </div>
                )}
                {payment.finalFiatAmount && (
                  <div>
                    <Label className="text-muted-foreground">Valor Recebido</Label>
                    <p className="text-lg font-bold text-green-600">
                      {formatCurrencyValue(payment.finalFiatAmount, payment.currency)}
                    </p>
                  </div>
                )}
                {payment.providerActualFee && (
                  <div>
                    <Label className="text-muted-foreground">Fee Real Provedor</Label>
                    <p className="text-lg font-medium text-muted-foreground">
                      {formatUsdValue(payment.providerActualFee)}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notas */}
        {(payment.adminNotes || payment.companyNotes) && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Notas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {payment.companyNotes && (
                <div>
                  <Label className="text-muted-foreground">Notas da Empresa</Label>
                  <p className="text-sm mt-1 bg-muted/30 p-3 rounded">{payment.companyNotes}</p>
                </div>
              )}
              {payment.adminNotes && (
                <div>
                  <Label className="text-muted-foreground">Notas do Administrador</Label>
                  <p className="text-sm mt-1 bg-muted/30 p-3 rounded">{payment.adminNotes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Audit History Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Histórico
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Created */}
            <TimelineItem
              date={payment.createdAt}
              title="Pagamento criado"
              description={`Valor: ${formatCurrencyValue(payment.amount, payment.currency)} via ${RAIL_LABELS[payment.paymentRail] || payment.paymentRail}`}
              completed
            />

            {/* Approved */}
            {payment.approvedAt && (
              <TimelineItem
                date={payment.approvedAt}
                title="Pagamento aprovado"
                description="Aprovado pelo administrador"
                completed
              />
            )}

            {/* Rejected */}
            {payment.rejectedAt && (
              <TimelineItem
                date={payment.rejectedAt}
                title="Pagamento rejeitado"
                description={payment.rejectionReason || "Rejeitado pelo administrador"}
                failed
              />
            )}

            {/* Awaiting deposit */}
            {payment.depositAddress && !payment.depositTxHash && payment.status === "AWAITING_DEPOSIT" && (
              <TimelineItem
                title="Aguardando depósito USDC"
                description={`Depositar ${formatUsdcAmount(payment.depositAmount)} no endereço do provedor`}
                pending
              />
            )}

            {/* Deposited */}
            {payment.depositTxHash && (
              <TimelineItem
                title="USDC depositado"
                description={`Hash: ${payment.depositTxHash.slice(0, 16)}...`}
                completed
              />
            )}

            {/* Completed */}
            {isCompleted && (
              <TimelineItem
                date={payment.updatedAt}
                title="Pagamento concluído"
                description={
                  payment.finalFiatAmount
                    ? `Beneficiário recebeu ${formatCurrencyValue(payment.finalFiatAmount, payment.currency)}`
                    : "Payout processado com sucesso"
                }
                completed
              />
            )}

            {/* Failed */}
            {payment.status === "FAILED" && (
              <TimelineItem
                date={payment.updatedAt}
                title="Pagamento falhou"
                description="Erro no processamento do payout"
                failed
              />
            )}

            {/* Cancelled */}
            {payment.status === "CANCELLED" && (
              <TimelineItem
                date={payment.updatedAt}
                title="Pagamento cancelado"
                description="Cancelado pela empresa"
                failed
              />
            )}

            {/* Pending states */}
            {payment.status === "PENDING_APPROVAL" && (
              <TimelineItem
                title="Aguardando aprovação"
                description="Pagamento pendente de revisão"
                pending
              />
            )}

            {payment.status === "PROCESSING" && (
              <TimelineItem
                title="Processando"
                description="Obtendo cotação e criando payout no provedor"
                pending
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aprovar Pagamento</DialogTitle>
            <DialogDescription>
              Confirma a aprovação do pagamento{" "}
              <strong>{payment.paymentCode}</strong>?
              <span className="block mt-1">
                Valor: {formatCurrencyValue(payment.amount, payment.currency)} via{" "}
                {RAIL_LABELS[payment.paymentRail] || payment.paymentRail}
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Notas do administrador (opcional)</Label>
              <Textarea
                value={approveNotes}
                onChange={(e) => setApproveNotes(e.target.value)}
                placeholder="Adicione observações sobre esta aprovação..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowApproveDialog(false)}
              disabled={actionLoading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleApprove}
              disabled={actionLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Aprovar Pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar Pagamento</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja rejeitar o pagamento{" "}
              <strong>{payment.paymentCode}</strong>? O saldo USDC bloqueado será
              desbloqueado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">
                Motivo da rejeição <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Descreva o motivo da rejeição..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRejectDialog(false)}
              disabled={actionLoading}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={actionLoading || !rejectReason.trim()}
            >
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Rejeitar Pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Timeline Item Component ──────────────────────────────

function TimelineItem({
  date,
  title,
  description,
  completed,
  pending,
  failed,
}: {
  date?: string;
  title: string;
  description: string;
  completed?: boolean;
  pending?: boolean;
  failed?: boolean;
}) {
  return (
    <div className="flex items-start gap-4">
      <div
        className={cn(
          "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
          completed && "bg-green-100",
          pending && "bg-orange-100",
          failed && "bg-red-100",
          !completed && !pending && !failed && "bg-muted"
        )}
      >
        {completed ? (
          <CheckCircle className="w-4 h-4 text-green-600" />
        ) : pending ? (
          <Clock className="w-4 h-4 text-orange-600" />
        ) : failed ? (
          <XCircle className="w-4 h-4 text-red-600" />
        ) : (
          <div className="w-2 h-2 rounded-full bg-muted-foreground" />
        )}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <p className="font-medium">{title}</p>
          {date && (
            <span className="text-sm text-muted-foreground">{formatDate(date)}</span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}
